import React, { useMemo, useState } from 'react';
import { Recipe, RecipeIngredient } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';
import './Sections.css';

// Recipe Master (add_recipee_layout.xml + recipe_list_frag_layout.xml): a
// sellable product is built from ingredient lines. Selling the product deducts
// each ingredient's stock (handled in PosContext.recordSale).
const Recipes: React.FC = () => {
  const { recipes, products, addRecipe, updateRecipe, deleteRecipe, updateProduct, formatMoney, isOn } = usePos();
  const [editing, setEditing] = useState<Recipe | null>(null);
  const [listSearch, setListSearch] = useState('');

  const [name, setName] = useState('');
  const [productId, setProductId] = useState('');
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [ingSearch, setIngSearch] = useState('');
  const [ingQty, setIngQty] = useState('1');

  const filtered = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    if (!t) return recipes;
    return recipes.filter((r) => r.name.toLowerCase().includes(t));
  }, [recipes, listSearch]);

  const ingResults = useMemo(() => {
    const t = ingSearch.trim().toLowerCase();
    if (!t) return [];
    return products
      .filter((p) => p.name.toLowerCase().includes(t) || p.barcode?.includes(ingSearch))
      .slice(0, 8);
  }, [ingSearch, products]);

  const productName = (id: string) => products.find((p) => p.id === id)?.name ?? '';
  const totalCost = ingredients.reduce((s, i) => s + i.unitCost * i.quantity, 0);

  const reset = () => {
    setEditing(null);
    setName('');
    setProductId('');
    setIngredients([]);
    setIngSearch('');
    setIngQty('1');
  };

  const addIngredient = (pid: string) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    const qty = parseFloat(ingQty) || 1;
    setIngredients((prev) => {
      const match = prev.find((i) => i.productId === pid);
      if (match) return prev.map((i) => (i.productId === pid ? { ...i, quantity: i.quantity + qty } : i));
      return [...prev, { productId: pid, name: p.name, quantity: qty, unitCost: p.costPrice ?? p.price }];
    });
    setIngSearch('');
    setIngQty('1');
  };

  const setIngQtyAt = (pid: string, qty: number) =>
    setIngredients((prev) => prev.map((i) => (i.productId === pid ? { ...i, quantity: Math.max(0, qty) } : i)).filter((i) => i.quantity > 0));

  const removeIng = (pid: string) => setIngredients((prev) => prev.filter((i) => i.productId !== pid));

  const save = () => {
    if (!name.trim()) return alert('Recipe name is required.');
    if (!productId) return alert('Select the product this recipe makes.');
    if (ingredients.length === 0) return alert('Add at least one ingredient.');
    const data = { name: name.trim(), productId, ingredients };
    if (editing) updateRecipe({ ...editing, ...data });
    else addRecipe(data);
    reset();
  };

  const edit = (r: Recipe) => {
    setEditing(r);
    setName(r.name);
    setProductId(r.productId);
    setIngredients(r.ingredients.map((i) => ({ ...i })));
  };

  const remove = () => {
    if (editing && window.confirm('Delete this recipe?')) {
      deleteRecipe(editing.id);
      reset();
    }
  };

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <input className="pm-list-search" placeholder="Search recipe" value={listSearch} onChange={(e) => setListSearch(e.target.value)} />
        <div className="pm-list-scroll">
          {filtered.map((r) => (
            <button key={r.id} className={`pm-list-item ${editing?.id === r.id ? 'sel' : ''}`} onClick={() => edit(r)}>
              <span className="pm-li-name">{r.name}</span>
              <span className="pm-li-meta">
                {productName(r.productId) || '—'} · {r.ingredients.length} ingredient{r.ingredients.length === 1 ? '' : 's'}
              </span>
            </button>
          ))}
          {filtered.length === 0 && <p className="muted" style={{ padding: 16 }}>No recipes.</p>}
        </div>
      </div>

      {/* form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>{editing ? 'Edit Recipe' : 'Add Recipe'}</span>
          <div className="pf-top-btns">
            {editing && <button className="pf-btn-orange" onClick={reset}>New</button>}
            {editing && <button className="pf-btn-orange" onClick={remove}>Delete</button>}
            <button className="pf-btn-orange" onClick={save}>{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r2">
            <div>{lbl('Recipe name *')}<input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div>
              {lbl('For Product *')}
              <select value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">Select product…</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="pf-section">Ingredients</div>
          <div className="pf-row r2" style={{ position: 'relative' }}>
            <div>
              {lbl('Search ingredient')}
              <input value={ingSearch} onChange={(e) => setIngSearch(e.target.value)} placeholder="Product name / barcode" />
              {ingResults.length > 0 && (
                <ul className="search-results">
                  {ingResults.map((p) => (
                    <li key={p.id} onClick={() => addIngredient(p.id)}>
                      <span>{p.name}</span>
                      <span className="res-price">{formatMoney(p.costPrice ?? p.price)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ maxWidth: 120 }}>
              {lbl('Qty')}
              <input type="number" min={0} step="any" value={ingQty} onChange={(e) => setIngQty(e.target.value)} />
            </div>
          </div>

          <table className="section-table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Item</th>
                <th className="right">Qty</th>
                <th className="right">Cost</th>
                <th className="right">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ingredients.length === 0 ? (
                <tr><td colSpan={5} className="muted">No ingredients added.</td></tr>
              ) : (
                ingredients.map((i) => (
                  <tr key={i.productId}>
                    <td>{i.name}</td>
                    <td className="right">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={i.quantity}
                        onChange={(e) => setIngQtyAt(i.productId, parseFloat(e.target.value) || 0)}
                        style={{ width: 70, textAlign: 'right' }}
                      />
                    </td>
                    <td className="right">{formatMoney(i.unitCost)}</td>
                    <td className="right">{formatMoney(i.unitCost * i.quantity)}</td>
                    <td className="right"><button className="row-del" onClick={() => removeIng(i.productId)}>×</button></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="cr-footer" style={{ marginTop: 10 }}>
            Recipe Cost: {formatMoney(totalCost)}
            {/* "Combo Setting / A. Combo Price Update": push the combined
                ingredient cost onto the linked product's selling price. */}
            {isOn('enable_combo_price_update') && productId && totalCost > 0 && (() => {
              const target = products.find((p) => p.id === productId);
              if (!target) return null;
              return (
                <button
                  className="pf-btn-teal"
                  style={{ marginLeft: 12 }}
                  onClick={() => {
                    if (window.confirm(`Set "${target.name}" price to ${formatMoney(totalCost)} (was ${formatMoney(target.price)})?`)) {
                      updateProduct({ ...target, price: +totalCost.toFixed(2) });
                    }
                  }}
                >
                  Update Combo Price → {formatMoney(totalCost)}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Recipes;
